import Chip from '@mui/material/Chip'
import Autocomplete from '@mui/material/Autocomplete'
import TextField from '@mui/material/TextField'
import { useCallback, useState } from 'react'
import { PersistedErrorHandler } from 'components/Formik/forms'
import useId from '@mui/utils/useId'
import { SxProps } from '@mui/material/styles'

// Split on commas, tab or newline
const DELIMITER_REGEX = /[,\t\n]+/

// Always strip control characters except \n and \t
// eslint-disable-next-line no-control-regex
const CONTROL_CHARS_REGEX = /[\u0000-\u0008\u000B-\u001F\u007F-\u009F]/g

const stripControlCharacters = (text) => text.replace(CONTROL_CHARS_REGEX, '')

/**
 * Keep only characters that match the provided allowlist regex.
 * Expects: non-global, per-character regex (e.g., /[A-Za-z0-9 _-]/ or /[\p{L}\p{N}\p{Zs}.-]/u)
 * Must also include the delimiters to accept, otherwise there will be no way to complete a chip
 */
const allowedCharactersOnly = (text: string, allowed?: RegExp | null) => {
  if (!allowed) return text
  return Array.from(text).filter(char => allowed.test(char)).join('')
}

const sanitize = (text: string, allowed?: RegExp | null) => allowedCharactersOnly(stripControlCharacters(text), allowed)

const normalizeChips = (chips: string[], allowed?: RegExp | null) => {
  return Array.from(new Set(
    chips
      .map(c => sanitize(String(c), allowed).trim())
      .filter(Boolean)
  ))
}

interface FreeTextMultiSelectProps {
  label: string
  value: string[]
  setValue: (newValue: string[]) => void
  path?: string
  disabled?: boolean
  options?: string[]
  placeholder?: string
  allowedCharactersRegex?: RegExp | null
  error?: boolean
  required?: boolean
  onFocus?: () => void
  sx?: SxProps
}

export default function FreeTextMultiSelect({
  label,
  value,
  setValue,
  path = '',
  disabled = false,
  options = [],
  placeholder = '',
  allowedCharactersRegex = null,
  error = false,
  required = false,
  onFocus = () => {},
  sx = {},
}: FreeTextMultiSelectProps) {
  const autoId = useId()
  const [inputValue, setInputValue] = useState('')

  const handleChange = useCallback((_e: unknown, newChipValues: string[]) => {
    setValue(normalizeChips(newChipValues, allowedCharactersRegex))
  }, [allowedCharactersRegex, setValue])

  // Per-keystroke enforcement in the text input
  const handleInputChange = useCallback((_e: unknown, value: string, reason: string) => {
    const finalValue = reason === 'input' ? sanitize(value, allowedCharactersRegex) : value

    setInputValue(finalValue)
  }, [allowedCharactersRegex])

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault()
    const raw = e.clipboardData.getData('text/plain') ?? ''
    const cleaned = sanitize(raw, allowedCharactersRegex).trim()

    const newChips = cleaned.split(DELIMITER_REGEX).filter(Boolean)
    if (newChips.length === 0) return

    const merged = normalizeChips([...(value ?? []), ...newChips], allowedCharactersRegex)
    setValue(merged)
    setInputValue('')
  }

  return (
    <Autocomplete
      multiple
      id={`${label || 'chips'}_${autoId}`}
      options={options}
      value={value}
      inputValue={inputValue}
      onInputChange={handleInputChange}
      freeSolo
      onChange={handleChange}
      autoSelect
      onFocus={onFocus}
      sx={{
        '.MuiAutocomplete-inputRoot': {minHeight: '48px'},
        '.MuiAutocomplete-input': {paddingY: '2.5px !important'},
        width: '100%',
        ...sx
      }}
      renderTags={(value, getTagProps) =>
        value.map((option, index) => (
          <Chip
            color="default"
            size="small"
            variant="filled"
            label={option}
            {...getTagProps({ index })}
            key={index}
          />
        ))
      }
      renderInput={(params) => {
        const textField = (
          <TextField
            {...params}
            label={label}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            error={error}
            onPaste={handlePaste}
          />
        )
        if(path.length > 0) {
          return (
            <PersistedErrorHandler name={path}>
              {textField}
            </PersistedErrorHandler>
          )
        } else {
          return textField
        }
      }}
    />
  )
}
